import React, { useState, useRef, useEffect } from 'react';
import { 
  X, Send, Sparkles, Eraser, ThumbsUp, ThumbsDown, BotMessageSquare
} from 'lucide-react';
import ReactMarkdown from "react-markdown";
import './chatbot.css';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTE_LINK_LOCATION } from "../../../constants/external-constants";
import { ChatbotActions } from '../../../store/common/chatbot/chatbot.reducer';
import { invoiceAuditRulesActions } from "../../../store/external/invoice/invoice/audit-table.reducer";

const ChatbotWidget = () => {
  const isOpen = useSelector((state) => state.chatbot.isOpen); 
  
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]); 
  const [isGenerating, setIsGenerating] = useState(false);
  const messagesEndRef = useRef(null);

  const dispatch = useDispatch();
  const aiResponse = useSelector((state) => state.chatbot.responceMessage || []);
  const hasSpecialTask = useSelector((state) => state.chatbot.hasSpecialTask);
  const isLoading = useSelector((state) => state.chatbot.isLoading);  
  const actionKey = useSelector((state) => state.chatbot.actionKey);
  const actionLabel = useSelector((state) => state.chatbot.actionLabel);

 const SPECIAL_TASK_TEXT = "Generate summary report";

 const auditId = useSelector((state) => state.chatbot.auditId || 0);
 const exceptionId = useSelector((state) => state.chatbot.exceptionId || 0);
 const invoiceId = useSelector((state) => state.chatbot.invoiceId || 0);
 var isGenerateSummary = useSelector((state) => state.chatbot.isGenerateSummary || false);
 var getInvoiceDetailId = useSelector((state) => state.chatbot.getInvoiceDetailId || false);

 const currentAuditPageIndex = useSelector(state =>
    state.invoiceAuditRules?.invoiceAuditRulesItems?.Pagination?.PageIndex ??
    state.invoiceAuditRules?.invoiceAuditRulesItems?.Pagination?.PageNumber ??
    0
    );

  const isTaskSubmitted = messages.some(msg => msg.text === SPECIAL_TASK_TEXT && msg.sender === 'user');

  const userStr = sessionStorage.getItem('User');
  let firstName = null;
  if (userStr && userStr !== 'null' && userStr !== 'undefined') {
    try {
        const user = JSON.parse(userStr);
        firstName = user?.Employee?.FirstName ?? null;
    } catch (err) { console.error(err); }
  }
  const userInitial = firstName?.[0]?.toUpperCase() || 'U';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if(isOpen) scrollToBottom();
  }, [messages, isGenerating, isOpen]);

  useEffect(() => {
    if (isLoading) return;
    if (!isGenerating || !aiResponse) return;
    const timeoutId = setTimeout(() => {
      const newAiMsg = { id: Date.now(), text: aiResponse, sender: 'ai', actionKey: actionKey, actionLabel: actionLabel };
      setMessages((prev) => [...prev, newAiMsg]);
      setIsGenerating(false);
    }, 1500);
      return () => clearTimeout(timeoutId);
  }, [aiResponse, isGenerating, isLoading, actionKey, actionLabel]); 

    const handleClear = () => {
        if (hasSpecialTask) {
            isGenerateSummary = true;
        }
        else {
            isGenerateSummary = false;
        }
    dispatch(ChatbotActions.clearMessage(auditId, exceptionId, isGenerateSummary, invoiceId, getInvoiceDetailId));
    setIsGenerating(false);
    setInputValue('');
    setMessages([]);
  };

    const handleSend = async (text) => {
        const messageText = text || inputValue;
        if (!messageText.trim()) return;
        const newUserMsg = { id: Date.now(), text: messageText, sender: 'user' };
        setMessages(prev => [...prev, newUserMsg]);
        setInputValue('');
        setIsGenerating(true);

        const res = await dispatch(
            ChatbotActions.sendMessage(messageText, auditId, exceptionId, isGenerateSummary, invoiceId, getInvoiceDetailId)
        );

        // use returned response (best: no stale step/actionIntent)
        if ((res?.actionIntent || '').toLowerCase() === 'create_dispute') {
            dispatch(invoiceAuditRulesActions.getInvoiceAuditRules(invoiceId, currentAuditPageIndex));
        }
    };

  const handleToggle = (status) => {
      dispatch(ChatbotActions.toggleChat(status));
    };

  const handleChatbotAction = (action, invoiceID, invoiceAuditID) => {
      if (action === "CREATE_NEW_CIRCUIT") {
          window.open(`${ROUTE_LINK_LOCATION.inventoryCreateCircuitLink}/${invoiceID}/${invoiceAuditID}`, "_blank")
    }
  };

  return (
    <div className="chat-widget-container font-sans">
        
        <div className={`chat-window shadow-lg ${isOpen ? 'open' : ''}`}>
          
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white">
            <div>
              <h5 className="mb-0 fw-bold text-dark"><span>AI</span><span className="chatbot-secondary__title">TEM</span></h5>
              <small className="sub-title text-muted d-flex align-items-center gap-1">
                Empowered by Azure Open AI <Sparkles size={12} className="text-primary" />
              </small>
            </div>
            <div className="d-flex gap-2">
               <button 
                    className="btn btn-sm text-secondary text-decoration-none icon-btn-hover" 
                    title="Clear" 
                    onClick={handleClear}
                    disabled={isLoading}
                >
                    <Eraser size={18}/>
                </button>                
                
                <button 
                    className="btn btn-sm text-secondary text-decoration-none icon-btn-hover"
                    title="Close"
                    onClick={() => handleToggle(false)}
                >
                    <X size={22} />
                </button>
            </div>
          </div>

          <div className="flex-grow-1 bg-light p-3 overflow-auto chat-body-scroll d-flex flex-column">
             
             {messages.length === 0 && !isGenerating && (
              <div className="mt-3 fade-in">
                <div className="mb-4">
                  <p className="fs-5 fw-semibold text-dark mb-1">How can I help?</p>
                  <div className="chat-message-header d-flex align-items-center gap-2 text-muted">
                    <span>Is this response helpful?</span>
                    <ThumbsUp size={12} style={{cursor:'pointer'}} />
                    <ThumbsDown size={12} style={{cursor:'pointer'}} />
                  </div>
                </div>
              </div>
            )}
            
            {messages.map((msg) => (
                  <div key={msg.id} className={`d-flex mb-2 ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                    {msg.sender === 'ai' && <div className="chat-avatar chat-avatar-ai shadow-sm"><BotMessageSquare size={18} /></div>}
                    <div className={`msg-bubble shadow-sm ${msg.sender === 'user' ? 'msg-user' : 'msg-ai'}`}>

                        <ReactMarkdown
                            components={{
                                a: ({ node, children, ...props }) => (
                                    <a {...props} target="_blank" rel="noopener noreferrer">
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {msg.text}
                        </ReactMarkdown>

                        {msg.sender === 'ai' && msg.actionKey !== null && msg.actionKey !== undefined && (
                                <button
                                    key={msg.actionKey}
                                    className="chatbot-link"
                                    onClick={() => handleChatbotAction(msg.actionKey, invoiceId, auditId)}
                                > {msg.actionLabel}
                                </button>
                    )}</div>
                      {msg.sender === 'user' && <div className="chat-avatar chat-avatar-user shadow-sm">{userInitial}</div>}
                  </div>
            ))}

            {isGenerating && (
              <div className="chat-card__container card border-0 shadow-sm p-3 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <span className="text-muted small fw-medium">Thinking...</span>
                </div>
              </div>
            )}

            {hasSpecialTask && !isTaskSubmitted && !isGenerating && (
                <div className="d-flex justify-content-end mb-3 mt-2 fade-in">
                  <button className="btn suggestion-chip py-2 px-3 rounded-3 shadow-sm"
                    onClick={() => handleSend(SPECIAL_TASK_TEXT)}>
                    Do you want to generate summary report?
                  </button>
                </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-top bg-white">             
             <div className="position-relative mb-2">
              <textarea
                className="form-control chat-input rounded-4 pe-5"
                rows="2"
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { 
                    if (isLoading && e.key === "Enter") { e.preventDefault(); return; }
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
              ></textarea>
              <div className="position-absolute bottom-0 end-0 p-2 top--4">
                <button 
                  className={`btn btn-sm btn-submit__message rounded-circle d-flex align-items-center justify-content-center ${inputValue.trim() ? 'aitem-gradient-bg' : 'btn-light text-muted'}`}                    
                  onClick={() => handleSend()} disabled={!inputValue.trim() || isLoading}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
            <div className="d-flex justify-content-center align-items-center ps-1">
              <small className="text-muted" style={{fontSize: '10px'}}>Azure Open AI uses GPT-4o AI model. Always review content for mistakes</small>
            </div>
          </div>
        </div>

        <button 
          className={`chat-trigger-btn aitem-gradient-bg ${isOpen ? 'hide-btn' : ''}`}
          onClick={() => handleToggle(true)}
        >
          <Sparkles size={28} />
        </button>

    </div>
  );
};
export default ChatbotWidget;